/**
 * The filter row over the project register: what to search for, which projects
 * are meant, and which of the two views draws the answer.
 *
 * Split out of `ProjectsList` before the round that added the client, deadline,
 * tag, member and archive filters. That round is this one, and the split earned
 * itself: seven controls plus their option lists would have doubled a screen
 * file that already carries the list, the board, the pager and two dialogs.
 *
 * It holds no state of its own. What is being asked for arrives as one
 * `ListView` - the same object the address bar is read into - and every change
 * leaves as a patch, so the screen can write the page reset in the SAME update
 * as the filter. A prop pair per control is the arrangement where somebody adds
 * the eighth filter and forgets to thread the reset through it.
 *
 * The search box is the one exception: it is handed the operator's typing
 * buffer rather than the committed query, because the buffer belongs with the
 * debounce that commits it, and a field re-reading the committed value would
 * drop the letters typed during the wait.
 *
 * ── WHICH FILTERS THE ROADMAP GETS ─────────────────────────────────────────
 *
 * Status is hidden there and only status. The board draws every status as a
 * column of its own, so filtering by one would empty three of the four columns
 * and explain nothing. Every other filter narrows a board exactly as it narrows
 * a list - "this client's roadmap", "what Mari is on", "what is due in March" -
 * and `listParams` sends them for both views, so what is on screen matches the
 * controls. A filter that is visible on the roadmap and quietly does nothing
 * would be worse than one that is absent.
 *
 * ── WHY THE ROW IS FOLDED ──────────────────────────────────────────────────
 *
 * Seven controls drawn at once cost two rows above the register on a laptop, and
 * on most days every one of them is sitting at its default. The register is the
 * busiest screen in the product; what it owes the operator first is ROWS.
 *
 * So the search box, the saved views and the two view shapes stay out, and the
 * six controls that narrow the SET fold behind one toggle. Two rules keep that
 * fold honest, and they are the whole reason this is not merely a hidden panel:
 *
 *   - The toggle CARRIES THE COUNT of what is still on. A narrowed register with
 *     the row shut and nothing on screen admitting it is the same failure the
 *     reset button already exists to prevent, moved one fold deeper.
 *   - It OPENS BY ITSELF when the screen is entered already filtered - from a
 *     saved view, from somebody else's link, from a reload inside March. Then
 *     the reason the list is short is the first thing on the page rather than
 *     something the operator has to go and uncover.
 */
import { useMemo, useState } from "react";
import { ChevronDown, GanttChartSquare, List, SlidersHorizontal, X } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { ProjectStatus } from "../../../lib/projects";
import { staffOptionLabel } from "../../../lib/staff";
import { Btn, MenuField, PickerField, PillTabs, SearchField, Toolbar, ToolbarGap, type PickerOption } from "../../ui";
import { useStaff } from "../_shared/staff";
import { DeadlineFilter } from "./DeadlineFilter";
import { useClientOptions, useProjectTags } from "./filterOptions";
import {
  ARCHIVED_MODES,
  VIEWS,
  activeFilterCount,
  isFiltered,
  type ArchivedMode,
  type ListView,
  type View,
} from "./listView";
import { SavedViews } from "./SavedViews";
import { STATUSES } from "./status";

// `VIEWS` and `View` moved to `listView.ts`, which is the file that parses
// `?view=` and therefore has to own the list of legal values without importing a
// component to get it. Re-exported from here because that is where the rest of
// this section already looks for them, and a rename across the callers is not
// what that move was.
export { VIEWS, type View };

/** A `Record`, not a ternary: a view added to `VIEWS` without a label is then a
 *  type error rather than a pill with nothing written on it. */
const VIEW_LABEL: Record<View, string> = {
  list: "projects.roadmap.viewList",
  roadmap: "projects.roadmap.viewBoard",
};

/**
 * The glyph each view wears, now that the pills carry no wording.
 *
 * A list or a roadmap is a SHAPE, not a subject, and the two shapes say
 * themselves faster than the two words did - the same argument the media
 * section's grid/rows pair makes. The wording is not thrown away: `PillTabs`
 * hands it to the accessible name and the tooltip, so a screen reader and a
 * hovering pointer both still get the word.
 *
 * A `Record` for the same reason the labels are one: a third view added to
 * `VIEWS` is then a type error here rather than an empty pill.
 */
const VIEW_ICON: Record<View, typeof List> = {
  list: List,
  roadmap: GanttChartSquare,
};

/**
 * What each side of the archive line is called.
 *
 * Three entries because the API has three answers, and they are three genuinely
 * different questions rather than a checkbox: the daily register, the archive
 * opened on purpose, and everything at once. A "show archived" tick box was the
 * alternative and it cannot say the middle one - it would leave "only" reachable
 * from a saved link and unreachable from the control that claims to own it,
 * which is the state where the toolbar and the list stop agreeing.
 */
/**
 * The three answers, as three statements about the SET rather than as two
 * statements and a toggle.
 *
 * `all` says "All" and not "Show archived", which is what it said first and
 * which read wrongly in the menu it lives in: beside "Not archived" and
 * "Archived" a third entry phrased as an instruction looks like a switch that
 * turns something on, so the operator cannot tell whether picking it ADDS the
 * archived rows to the ones they already have or REPLACES them. Every other
 * filter in this toolbar names its set ("All statuses", "All clients"), and this
 * one now does too. `projects.showArchived` keeps its own wording for the place
 * a toggle is the right shape.
 */
const ARCHIVED_LABEL: Record<ArchivedMode, string> = {
  hide: "projects.filterNotArchived",
  only: "projects.archived",
  all: "projects.filterAll",
};

export function ProjectsToolbar({
  q,
  onQ,
  filters,
  onPatch,
  onClear,
}: {
  /** The typing buffer, not the committed query - see the note at the top. */
  q: string;
  onQ: (v: string) => void;
  /** Everything the register is being asked for, including the view and the
   *  sort: they decide which controls are drawn, even though no control here
   *  writes the sort. */
  filters: ListView;
  /** One change. The screen adds the page reset and writes the address bar. */
  onPatch: (change: Partial<ListView>) => void;
  /** Every filter off at once. Its own prop and not a patch, because "no
   *  filters" is a statement about a set of fields this component must not have
   *  to enumerate - `clearFilters` in `listView.ts` owns that list, and the day
   *  an eighth filter is added the reset has to follow it without an edit
   *  here. */
  onClear: () => void;
}) {
  const t = useT();
  const { staff } = useStaff();
  const tags = useProjectTags();
  const clients = useClientOptions();
  const list = filters.view === "list";
  const narrowed = activeFilterCount(filters);
  /**
   * Whether the folded row is showing.
   *
   * Seeded ONCE, from the question the screen was entered with. It is not
   * derived from `filters`, and that is the point: a derived flag would slam the
   * row shut under the operator's hand the moment they cleared the last filter,
   * which is exactly when they are most likely to set another one.
   */
  const [showFilters, setShowFilters] = useState(() => isFiltered(filters));

  /**
   * The people who can be on a project, with the leavers marked.
   *
   * `useStaff` returns them too, sorted last, and that is deliberate: they are
   * still members of last year's projects, so filtering by one has to stay
   * possible even though giving them new work does not. What must not happen is
   * a former colleague sitting in the list looking like an ordinary choice -
   * hence `staffOptionLabel`, which is the only place the word fits in a control
   * that draws its rows from a label and a hint.
   */
  const members = useMemo<PickerOption[]>(
    () => [
      { value: "", label: t("projects.filterMember") },
      ...staff.map((s) => ({
        value: s.id,
        label: staffOptionLabel(s, t("common.inactive")),
        hint: s.email,
      })),
    ],
    [staff, t],
  );

  const clientOptions = useMemo<PickerOption[]>(
    () => [
      { value: "", label: t("projects.filterClient") },
      ...clients.map((c) => ({ value: c.id, label: c.name })),
    ],
    [clients, t],
  );

  const tagOptions = useMemo<PickerOption[]>(
    () => [{ value: "", label: t("projects.filterTag") }, ...tags.map((tag) => ({ value: tag, label: tag }))],
    [tags, t],
  );

  return (
    <>
      {/* Row one: what the operator types, what is folded away, and how the
          answer is drawn. Nothing here narrows the set except the search box,
          which keeps its place because it is the control people reach for
          first and because a search term is visible in the field itself. */}
      <Toolbar className={cn(showFilters && "mb-2.5")}>
        <SearchField value={q} onChange={onQ} placeholder={t("projects.search")} />

        {/* Wearing the count of what is still on, so a shut row can never hide
            a narrowed register. `is-on` while it is open as well as while it is
            filtered: the pill has to look pressed when it IS pressed, or the
            row below looks like it opened by itself. */}
        <button
          type="button"
          className={cn(
            "crm-fld crm-fld-sel w-auto min-w-0 gap-2.5 px-4 text-[12.75px]",
            (showFilters || narrowed > 0) && "is-on",
          )}
          aria-expanded={showFilters}
          aria-controls="projects-filter-row"
          onClick={() => setShowFilters((v) => !v)}
        >
          <SlidersHorizontal className="size-4" />
          <span>{t("projects.filters")}</span>
          {narrowed > 0 ? <span className="crm-fld-n">{narrowed}</span> : null}
          <ChevronDown className={cn("size-4 transition-transform", showFilters && "rotate-180")} />
        </button>

        {/* Up here rather than in the folded row, and only over a register that
            is actually narrowed. Putting it below would mean an operator who
            shut the row had to open it again to undo what the count is telling
            them about. */}
        {isFiltered(filters) ? (
          <Btn small onClick={onClear}>
            <X className="size-4" />
            {t("projects.clearFilters")}
          </Btn>
        ) : null}

        <ToolbarGap />

        {/* Past the gap, beside the view tabs, so the right-hand end of the row
            reads as "which saved question, drawn how" while the controls that
            BUILD a question stay together on the left.

            It holds its own list of saved views, which is not a break with the
            note at the top of this file: that list is data fetched from the API,
            not a second copy of what the register is being asked for. The
            question still arrives as `filters` and still leaves as a patch - a
            whole one, because applying a saved view replaces every field
            including the page and the search term.

            Drawn on the roadmap too. A view saved from the list carries
            `view: "list"`, so applying one there switches back - which is right:
            the operator asked for that view, and the shape is part of it. */}
        <SavedViews filters={filters} onApply={onPatch} />

        <PillTabs
          value={filters.view}
          onChange={(view: View) => onPatch({ view })}
          iconOnly
          tabs={VIEWS.map((v) => {
            const Icon = VIEW_ICON[v];
            return { value: v, label: t(VIEW_LABEL[v]), icon: <Icon className="size-4" /> };
          })}
        />
      </Toolbar>

      {/* Row two: the six that narrow the set. Unmounted rather than hidden, so
          the client, tag and member pickers do not sit there holding option
          lists nobody has asked to see. */}
      {showFilters ? (
        <Toolbar id="projects-filter-row">
          {/* The roadmap draws every status as a column of its own, so filtering
              by one status there would empty three of the four columns and
              explain nothing. The control is not disabled but absent: a greyed
              field on a board that never uses it is a question the operator has
              to answer. */}
          {list ? (
            <MenuField
              value={filters.status}
              onChange={(status: ProjectStatus | "all") => onPatch({ status })}
              label={t("projects.statusCol")}
              options={[
                { value: "all" as const, label: t("projects.allStatuses") },
                ...STATUSES.map((s) => ({ value: s, label: t(`projects.status.${s}`) })),
              ]}
            />
          ) : null}

          {/* A picker rather than a menu, because this list is long enough to
              hunt through: it opens on a search box and filters as you type. Its
              options are the first hundred clients by name - see
              `CLIENT_LIMIT`. */}
          <PickerField
            variant="toolbar"
            value={filters.clientId}
            onChange={(clientId) => onPatch({ clientId })}
            options={clientOptions}
            label={t("projects.client")}
            placeholder={t("projects.filterClient")}
            searchPlaceholder={t("projects.clientPlaceholder")}
            emptyLabel={t("palette.empty")}
          />

          <DeadlineFilter
            from={filters.deadlineFrom}
            to={filters.deadlineTo}
            onChange={(window) => onPatch(window)}
          />

          <PickerField
            variant="toolbar"
            value={filters.tag}
            onChange={(tag) => onPatch({ tag })}
            options={tagOptions}
            label={t("projects.tags")}
            placeholder={t("projects.filterTag")}
            searchPlaceholder={t("projects.tags")}
            // Not "nothing matched your search": an empty vocabulary is the
            // normal state of a CRM where nobody has tagged anything yet, and
            // the menu has to say so rather than look broken.
            emptyLabel={t("palette.empty")}
          />

          <PickerField
            variant="toolbar"
            value={filters.member}
            onChange={(member) => onPatch({ member })}
            options={members}
            label={t("projects.members")}
            placeholder={t("projects.filterMember")}
            searchPlaceholder={t("common.search")}
            emptyLabel={t("palette.empty")}
          />

          {/* Applies to the board as well: an archived project has no business
              on a roadmap of what is running either. */}
          <MenuField
            value={filters.archived}
            onChange={(archived: ArchivedMode) => onPatch({ archived })}
            label={t("projects.archived")}
            options={ARCHIVED_MODES.map((m) => ({ value: m, label: t(ARCHIVED_LABEL[m]) }))}
          />
        </Toolbar>
      ) : null}
    </>
  );
}
